/**
 * In-app notification inbox — direct Firestore client access.
 *
 * The notification doc IS the inbox item (`tenants/{tenantId}/notifications/{id}`);
 * `readAt == null/absent` ⇒ unread. Notifications are written server-side
 * (Cloud Function / Cloud Tasks dispatcher); the client only reads its own
 * items and patches `readAt` on the docs it owns. firestore.rules (Phase 5)
 * allow the owner of the doc to read it and to patch `readAt`.
 *
 * Reads go straight to Firestore via the shared `praesto` Firebase app (the
 * same pattern as the Procedure SOP client), tenant-scoped and filtered by
 * `userId` so no cross-tenant / cross-user data is ever fetched.
 *
 * Firestore composite index required (the orderBy on a different field than the
 * equality filter forces it):
 *   collection group: notifications
 *   fields: userId ASC, createdAt DESC
 */
import type { Notification } from "@praesto/shared-types";
import { getApp } from "firebase/app";
import {
  type Firestore,
  collection,
  doc,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";

export type Unsubscribe = () => void;

const INBOX_PAGE_SIZE = 50;

function db(): Firestore {
  return getFirestore(getApp("praesto"));
}

const base = (tenantId: string) => `tenants/${tenantId}/notifications`;

/**
 * Subscribe to the current user's notifications, newest first (limited to the
 * most recent {@link INBOX_PAGE_SIZE}). The callback fires with the full
 * current list on every snapshot.
 */
export function subscribeNotifications(
  tenantId: string,
  userId: string,
  cb: (n: ReadonlyArray<Notification>) => void,
): Unsubscribe {
  const q = query(
    collection(db(), base(tenantId)),
    where("userId", "==", userId),
    orderBy("createdAt", "desc"),
    limit(INBOX_PAGE_SIZE),
  );
  return onSnapshot(q, (snap) => {
    // Spread `id` LAST so the document id always wins: a notification doc whose
    // payload omits `id` (or carries a stale value) must still expose the real
    // Firestore id, otherwise markNotificationRead — which keys off `n.id` —
    // would silently no-op.
    cb(snap.docs.map((d) => ({ ...d.data(), id: d.id }) as Notification));
  });
}

/**
 * Mark a notification as read by stamping `readAt` with the server time.
 * Idempotent: re-marking an already-read item simply re-stamps it.
 */
export async function markNotificationRead(tenantId: string, id: string): Promise<void> {
  await updateDoc(doc(db(), `${base(tenantId)}/${id}`), { readAt: serverTimestamp() });
}

/**
 * Mark a batch of notifications as read in a single atomic commit. Pass the ids
 * of the currently-unread items (the caller filters `readAt == null`); empty
 * input is a no-op. Firestore batches cap at 500 ops — the inbox is limited to
 * {@link INBOX_PAGE_SIZE} (50), so a single batch always suffices here.
 */
export async function markAllNotificationsRead(
  tenantId: string,
  ids: ReadonlyArray<string>,
): Promise<void> {
  if (ids.length === 0) return;
  const batch = writeBatch(db());
  const now = serverTimestamp();
  for (const id of ids) {
    batch.update(doc(db(), `${base(tenantId)}/${id}`), { readAt: now });
  }
  await batch.commit();
}
